import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Car, Plus, Trash2, Wrench, Calendar, Gauge, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const SERVICE_TYPES = ["Oil change", "Tire rotation", "Brake service", "Battery", "Coolant", "MOT/Inspection", "General service", "Other"];

export default function Vehicles() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [logs, setLogs] = useState({});
  const [logOpen, setLogOpen] = useState(false);
  const [logVehicle, setLogVehicle] = useState(null);
  const blank = {
    name: "", make: "", model: "", year: "", plate: "", mileage: "",
    last_service_date: "", next_service_date: "", registration_renewal: "",
    insurance_renewal: "", notes: "",
  };
  const [form, setForm] = useState(blank);
  const logBlank = { date: new Date().toISOString().slice(0, 10), type: "Oil change", cost: "", mileage: "", provider: "", notes: "" };
  const [logForm, setLogForm] = useState(logBlank);

  const load = useCallback(async () => { const { data } = await api.get("/vehicles"); setItems(data); }, []);
  useEffect(() => { load(); }, [load]);

  const loadLogs = async (vid) => {
    const { data } = await api.get(`/vehicles/${vid}/service-logs`);
    setLogs((l) => ({ ...l, [vid]: data }));
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Vehicle name required");
    try {
      const payload = {
        ...form,
        year: form.year ? parseInt(form.year) : null,
        mileage: form.mileage ? parseInt(form.mileage) : null,
      };
      if (editId) await api.put(`/vehicles/${editId}`, payload);
      else await api.post("/vehicles", payload);
      setOpen(false); setEditId(null); setForm(blank); toast.success("Saved"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const startEdit = (it) => {
    setForm({ ...blank, ...it, year: it.year ?? "", mileage: it.mileage ?? "" });
    setEditId(it.id); setOpen(true);
  };
  const remove = async (id) => { await api.delete(`/vehicles/${id}`); load(); };

  const expand = async (v) => {
    if (expanded === v.id) { setExpanded(null); return; }
    setExpanded(v.id);
    if (!logs[v.id]) await loadLogs(v.id);
  };
  const addLog = async () => {
    if (!logForm.date) return toast.error("Date required");
    try {
      const payload = {
        ...logForm,
        cost: logForm.cost ? parseFloat(logForm.cost) : null,
        mileage: logForm.mileage ? parseInt(logForm.mileage) : null,
      };
      await api.post(`/vehicles/${logVehicle.id}/service-logs`, payload);
      setLogOpen(false); setLogForm(logBlank); toast.success("Service logged");
      loadLogs(logVehicle.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeLog = async (vid, lid) => {
    await api.delete(`/vehicles/${vid}/service-logs/${lid}`);
    loadLogs(vid);
  };

  return (
    <div>
      <PageHeader
        eyebrow="Garage"
        title="Vehicles"
        subtitle="Service history, renewals, and mileage — for every set of wheels."
        action={
          <Button data-testid="veh-add-button" onClick={() => { setEditId(null); setForm(blank); setOpen(true); }}
            className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white px-5 h-10">
            <Plus size={15} className="mr-1.5" /> Add vehicle
          </Button>
        }
      />

      {items.length === 0 ? (
        <EmptyState icon={Car} title="No vehicles yet." message="Track service intervals, registration and insurance renewal dates so nothing lapses." />
      ) : (
        <div className="space-y-4">
          {items.map((v) => (
            <div key={v.id} data-testid={`veh-card-${v.id}`} className="warm-card overflow-hidden fade-up">
              <button onClick={() => expand(v)} className="w-full p-6 text-left flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-hearth-slate/12 text-hearth-slate grid place-items-center shrink-0">
                  <Car size={20} strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="caption">{[v.year, v.make, v.model].filter(Boolean).join(" ") || "Vehicle"}</p>
                  <h3 className="font-display text-3xl text-hearth-ink leading-tight">{v.name}</h3>
                  <div className="mt-3 flex flex-wrap gap-3 text-xs">
                    {v.plate && <span className="chip bg-hearth-stone text-hearth-mute font-mono">{v.plate}</span>}
                    {v.mileage != null && <span className="chip bg-hearth-stone text-hearth-mute inline-flex items-center gap-1"><Gauge size={11} /> {v.mileage.toLocaleString()} km</span>}
                    {v.next_service_date && <span className="chip bg-hearth-clay/12 text-hearth-clayDark inline-flex items-center gap-1"><Wrench size={11} /> Service {v.next_service_date}</span>}
                    {v.registration_renewal && <span className="chip bg-hearth-amber/15 text-[#7A5320] inline-flex items-center gap-1"><Calendar size={11} /> Reg {v.registration_renewal}</span>}
                    {v.insurance_renewal && <span className="chip bg-hearth-sage/15 text-hearth-sageDark inline-flex items-center gap-1"><Calendar size={11} /> Ins {v.insurance_renewal}</span>}
                  </div>
                </div>
                <ChevronDown size={18} className={`text-hearth-mute transition mt-1 ${expanded === v.id ? "rotate-180" : ""}`} />
              </button>

              {expanded === v.id && (
                <div className="border-t border-hearth-line/70 px-6 py-5 bg-hearth-stone/30">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-display text-2xl text-hearth-ink">Service log</h4>
                    <div className="flex gap-2">
                      <Button data-testid={`veh-add-log-${v.id}`} variant="ghost" onClick={() => { setLogVehicle(v); setLogForm(logBlank); setLogOpen(true); }} className="rounded-full text-sm">
                        <Plus size={14} className="mr-1" /> Log service
                      </Button>
                      <Button variant="ghost" data-testid={`veh-edit-${v.id}`} onClick={() => startEdit(v)} className="rounded-full text-sm">Edit</Button>
                      <Button variant="ghost" data-testid={`veh-delete-${v.id}`} onClick={() => remove(v.id)} className="rounded-full text-sm text-hearth-coral hover:bg-hearth-coral/10"><Trash2 size={13} className="mr-1" />Delete</Button>
                    </div>
                  </div>
                  {(logs[v.id] || []).length === 0 ? (
                    <p className="text-sm text-hearth-mute italic">No service entries yet.</p>
                  ) : (
                    <div className="divide-y divide-hearth-line/70">
                      {(logs[v.id] || []).map((l) => (
                        <div key={l.id} data-testid={`veh-log-${l.id}`} className="py-3 flex items-center gap-3 text-sm">
                          <div className="w-2 h-2 rounded-full bg-hearth-clay shrink-0" />
                          <div className="font-mono text-xs text-hearth-mute w-24">{fmtDate(l.date)}</div>
                          <div className="font-medium text-hearth-ink flex-1 truncate">{l.type}{l.provider ? ` · ${l.provider}` : ""}</div>
                          {l.mileage != null && <div className="text-xs text-hearth-mute w-24 text-right">{l.mileage.toLocaleString()} km</div>}
                          <div className="font-mono text-sm text-hearth-ink w-20 text-right">{money(l.cost)}</div>
                          <button onClick={() => removeLog(v.id, l.id)} className="p-1.5 rounded-md text-hearth-mute hover:text-hearth-coral hover:bg-hearth-coral/10"><Trash2 size={13} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl border-hearth-line bg-hearth-paper max-w-2xl">
          <DialogHeader><DialogTitle className="font-display text-3xl">{editId ? "Edit vehicle" : "Add a vehicle"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="caption">Name / nickname</Label>
              <Input data-testid="veh-form-name" className="input-warm mt-1.5 h-10" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Family Car" />
            </div>
            <div><Label className="caption">Make</Label><Input className="input-warm mt-1.5 h-10" value={form.make || ""} onChange={(e) => setForm({ ...form, make: e.target.value })} /></div>
            <div><Label className="caption">Model</Label><Input className="input-warm mt-1.5 h-10" value={form.model || ""} onChange={(e) => setForm({ ...form, model: e.target.value })} /></div>
            <div><Label className="caption">Year</Label><Input type="number" className="input-warm mt-1.5 h-10" value={form.year || ""} onChange={(e) => setForm({ ...form, year: e.target.value })} /></div>
            <div><Label className="caption">Plate</Label><Input className="input-warm mt-1.5 h-10 font-mono" value={form.plate || ""} onChange={(e) => setForm({ ...form, plate: e.target.value })} /></div>
            <div><Label className="caption">Mileage (km)</Label><Input type="number" className="input-warm mt-1.5 h-10" value={form.mileage || ""} onChange={(e) => setForm({ ...form, mileage: e.target.value })} /></div>
            <div><Label className="caption">Last service</Label><Input type="date" className="input-warm mt-1.5 h-10" value={form.last_service_date || ""} onChange={(e) => setForm({ ...form, last_service_date: e.target.value })} /></div>
            <div><Label className="caption">Next service due</Label><Input type="date" className="input-warm mt-1.5 h-10" value={form.next_service_date || ""} onChange={(e) => setForm({ ...form, next_service_date: e.target.value })} /></div>
            <div><Label className="caption">Registration renewal</Label><Input type="date" className="input-warm mt-1.5 h-10" value={form.registration_renewal || ""} onChange={(e) => setForm({ ...form, registration_renewal: e.target.value })} /></div>
            <div><Label className="caption">Insurance renewal</Label><Input type="date" className="input-warm mt-1.5 h-10" value={form.insurance_renewal || ""} onChange={(e) => setForm({ ...form, insurance_renewal: e.target.value })} /></div>
            <div className="md:col-span-2">
              <Label className="caption">Notes</Label>
              <Textarea className="input-warm mt-1.5" rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="veh-form-save" onClick={save} className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="rounded-2xl border-hearth-line bg-hearth-paper max-w-md">
          <DialogHeader><DialogTitle className="font-display text-3xl">Log a service</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="caption">Date</Label><Input data-testid="veh-log-date" type="date" className="input-warm mt-1.5 h-10" value={logForm.date} onChange={(e) => setLogForm({ ...logForm, date: e.target.value })} /></div>
              <div>
                <Label className="caption">Type</Label>
                <Select value={logForm.type} onValueChange={(v) => setLogForm({ ...logForm, type: v })}>
                  <SelectTrigger className="input-warm mt-1.5 h-10 rounded-xl" data-testid="veh-log-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{SERVICE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="caption">Cost</Label><Input data-testid="veh-log-cost" type="number" step="0.01" className="input-warm mt-1.5 h-10" value={logForm.cost} onChange={(e) => setLogForm({ ...logForm, cost: e.target.value })} /></div>
              <div><Label className="caption">Mileage</Label><Input type="number" className="input-warm mt-1.5 h-10" value={logForm.mileage} onChange={(e) => setLogForm({ ...logForm, mileage: e.target.value })} /></div>
            </div>
            <div><Label className="caption">Provider / garage</Label><Input className="input-warm mt-1.5 h-10" value={logForm.provider} onChange={(e) => setLogForm({ ...logForm, provider: e.target.value })} /></div>
            <div><Label className="caption">Notes</Label><Textarea className="input-warm mt-1.5" rows={2} value={logForm.notes} onChange={(e) => setLogForm({ ...logForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLogOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="veh-log-save" onClick={addLog} className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
